/**
 * Pagina "Limiti operativi" (SPEC-012 §9).
 *
 * Tre punti in cui la UI deve essere esplicita invece che comoda:
 *
 *  1. **L'ambito è il primo gesto.** La stessa soglia significa cose diverse
 *     su un portafoglio e sull'insieme dei portafogli, e un'operazione concorre
 *     sempre a entrambi: guardare un portafoglio senza sapere che esistono
 *     limiti globali è fuorviante, quindi la pagina lo dice.
 *  2. **Le combinazioni incoerenti sono disabilitate nel form**, non validate
 *     dopo l'invio: la finestra temporale sparisce quando si sceglie un limite
 *     sulla singola operazione, perché lì non esiste.
 *  3. **Il simulatore non invia niente.** È l'unico modo di collaudare una
 *     configurazione finché l'automazione non esiste, e la pagina lo dichiara
 *     invece di lasciar temere che stia comprando qualcosa.
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { usePortfolios } from '@/features/portfolios/api';
import { Alert, Button, Card, EmptyState, Field, Modal, Select, Spinner, Switch, TableWrap } from '@/ui';
import {
  useCreateLimit,
  useDeleteLimit,
  useSimulateOperation,
  useTradingLimits,
  useUpdateLimit,
  useUpdateLimitSettings,
  type CreateLimitInput,
  type LimitSide,
  type LimitType,
  type LimitWindow,
  type ScopeFilter,
  type TradingLimit,
} from './api';
import './trading-limits.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

const LIMIT_TYPE_LABEL: Record<LimitType, string> = {
  operation_count: 'Numero di operazioni',
  traded_amount: 'Importo movimentato',
  single_operation_amount: 'Importo di una singola operazione',
  single_operation_portfolio_pct: '% del portafoglio per singola operazione',
};

const SIDE_LABEL: Record<LimitSide, string> = {
  any: 'Tutte le operazioni',
  buy: 'Solo acquisti',
  sell: 'Solo vendite',
};

const WINDOW_LABEL: Record<LimitWindow, string> = {
  operation: 'Singola operazione',
  day: 'Giornaliera',
  month: 'Mensile',
};

/** I due tipi che si valutano sulla singola operazione e non hanno contatore. */
function isPointwise(limitType: LimitType): boolean {
  return limitType === 'single_operation_amount' || limitType === 'single_operation_portfolio_pct';
}

// -----------------------------------------------------------------------------
// Dialog di creazione
// -----------------------------------------------------------------------------

function NewLimitModal({ portfolioId, onClose }: { portfolioId: string | null; onClose: () => void }) {
  const create = useCreateLimit();
  const [limitType, setLimitType] = useState<LimitType>('operation_count');
  const [side, setSide] = useState<LimitSide>('any');
  const [windowUnit, setWindowUnit] = useState<LimitWindow>('day');
  const [perInstrument, setPerInstrument] = useState(false);
  const [threshold, setThreshold] = useState('');

  const pointwise = isPointwise(limitType);

  // Cambiare tipo può rendere impossibile la configurazione già scelta: si
  // riallinea qui invece di lasciare che il backend la rifiuti.
  const changeType = (next: LimitType) => {
    setLimitType(next);
    if (isPointwise(next)) {
      setWindowUnit('operation');
      setPerInstrument(false);
    } else if (windowUnit === 'operation') {
      setWindowUnit('day');
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const input: CreateLimitInput = {
      portfolioId,
      limitType,
      side,
      windowUnit: pointwise ? 'operation' : windowUnit,
      perInstrument: pointwise ? false : perInstrument,
      threshold: threshold.trim(),
    };
    create.mutate(input, { onSuccess: onClose });
  };

  const unitHint =
    limitType === 'operation_count'
      ? 'Numero intero di operazioni.'
      : limitType === 'single_operation_portfolio_pct'
        ? 'Percentuale da 0 a 100.'
        : 'Importo nella valuta base del tuo profilo.';

  return (
    <Modal title="Nuovo limite operativo" onClose={onClose}>
      <form className="trading-limits__form" onSubmit={handleSubmit}>
        <Select
          label="Cosa limitare"
          value={limitType}
          onChange={(e) => changeType(e.target.value as LimitType)}
          options={Object.entries(LIMIT_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
        />

        <Select
          label="A quali operazioni si applica"
          value={side}
          onChange={(e) => setSide(e.target.value as LimitSide)}
          options={Object.entries(SIDE_LABEL).map(([value, label]) => ({ value, label }))}
        />

        {!pointwise && (
          <Select
            label="Periodo"
            value={windowUnit}
            onChange={(e) => setWindowUnit(e.target.value as LimitWindow)}
            options={[
              { value: 'day', label: WINDOW_LABEL.day },
              { value: 'month', label: WINDOW_LABEL.month },
            ]}
            hint="Il contatore si azzera al cambio di periodo."
          />
        )}

        {!pointwise && (
          <Switch
            label="Conta separatamente per ogni titolo"
            checked={perInstrument}
            onChange={setPerInstrument}
          />
        )}

        <Field
          label="Soglia"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          numeric
          hint={unitHint}
          required
        />

        {create.isError && (
          <Alert variant="error" title="Limite non creato">
            {errorMessage(create.error, 'Errore imprevisto')}
          </Alert>
        )}

        <div className="trading-limits__form-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={create.isPending || threshold.trim() === ''}>
            {create.isPending ? 'Creazione…' : 'Crea limite'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Riga di limite
// -----------------------------------------------------------------------------

function ConsumptionCell({ limit }: { limit: TradingLimit }) {
  if (isPointwise(limit.limitType)) {
    return <span className="cell--minor">non cumulativo</span>;
  }

  if (limit.perInstrument) {
    if (limit.perInstrumentUsage.length === 0) {
      return <span className="cell--minor">nessun titolo nel periodo</span>;
    }
    return (
      <ul className="trading-limits__per-instrument">
        {limit.perInstrumentUsage.map((entry) => (
          <li key={entry.instrumentId}>
            <span>{entry.symbol ?? entry.name}</span>
            <strong>
              {entry.used} / {limit.threshold}
            </strong>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <span>
      <strong>{limit.used ?? '0'}</strong> / {limit.threshold}
      {limit.remaining !== null && <span className="cell--minor"> · residuo {limit.remaining}</span>}
    </span>
  );
}

function LimitRow({ limit }: { limit: TradingLimit }) {
  const update = useUpdateLimit();
  const remove = useDeleteLimit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(limit.threshold);

  const save = () => {
    update.mutate({ id: limit.id, threshold: draft.trim() }, { onSuccess: () => setEditing(false) });
  };

  return (
    <tr className={limit.enabled ? undefined : 'trading-limits__row--off'}>
      <td>
        <div className="trading-limits__desc">{limit.description}</div>
        <div className="cell--minor">
          {limit.scope === 'user' ? 'Globale (tutti i portafogli)' : (limit.portfolioName ?? 'Portafoglio')}
        </div>
      </td>
      <td className="col--secondary">
        {editing ? (
          <div className="trading-limits__inline-edit">
            <Field
              label="Soglia"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              numeric
            />
            <Button variant="ghost" onClick={save} disabled={update.isPending}>
              Salva
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(limit.threshold);
                setEditing(false);
              }}
            >
              Annulla
            </Button>
          </div>
        ) : (
          <button type="button" className="trading-limits__threshold" onClick={() => setEditing(true)}>
            {limit.threshold}
          </button>
        )}
      </td>
      <td>
        <ConsumptionCell limit={limit} />
      </td>
      <td className="col--tertiary">
        {limit.windowEndsAt ? (
          <span className="cell--minor">si azzera il {formatDateTime(limit.windowEndsAt)}</span>
        ) : (
          <span className="cell--minor">—</span>
        )}
      </td>
      <td>
        <div className="trading-limits__actions">
          <Switch
            label={limit.enabled ? 'Attivo' : 'Sospeso'}
            checked={limit.enabled}
            onChange={(checked) => update.mutate({ id: limit.id, enabled: checked })}
            disabled={update.isPending}
          />
          <Button
            variant="ghost"
            onClick={() => remove.mutate(limit.id)}
            disabled={remove.isPending}
          >
            Elimina
          </Button>
        </div>
        {update.isError && (
          <div className="trading-limits__row-error">{errorMessage(update.error, 'Aggiornamento non riuscito')}</div>
        )}
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// Simulatore
// -----------------------------------------------------------------------------

function Simulator({ portfolioId }: { portfolioId: string | null }) {
  const simulate = useSimulateOperation();
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [quantity, setQuantity] = useState('1');
  const [limitPrice, setLimitPrice] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    simulate.mutate({
      portfolioId,
      side,
      quantity: quantity.trim(),
      // Un ordine limite è l'unica forma che porta con sé un prezzo: senza uno
      // strumento a catalogo, un ordine a mercato non sarebbe valutabile e la
      // simulazione direbbe solo "controvalore ignoto".
      orderType: 'limit',
      limitPrice: limitPrice.trim(),
    });
  };

  const result = simulate.data;

  return (
    <Card
      title="Prova un'operazione"
      subtitle="Valutazione a secco: nessun ordine viene inviato e nessun contatore viene consumato."
    >
      <form className="trading-limits__sim" onSubmit={handleSubmit}>
        <Select
          label="Direzione"
          value={side}
          onChange={(e) => setSide(e.target.value as 'buy' | 'sell')}
          options={[
            { value: 'buy', label: 'Acquisto' },
            { value: 'sell', label: 'Vendita' },
          ]}
        />
        <Field
          label="Quantità"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          numeric
        />
        <Field
          label="Prezzo"
          value={limitPrice}
          onChange={(e) => setLimitPrice(e.target.value)}
          numeric
        />
        <Button type="submit" disabled={simulate.isPending || limitPrice.trim() === ''}>
          {simulate.isPending ? 'Valutazione…' : 'Valuta'}
        </Button>
      </form>

      {simulate.isError && (
        <Alert variant="error" title="Valutazione non riuscita">
          {errorMessage(simulate.error, 'Errore imprevisto')}
        </Alert>
      )}

      {result && !result.enforced && (
        <Alert variant="info" title="Nessun limite applicabile">
          In questo ambito non c'è alcun limite attivo per questa direzione: l'operazione non
          consumerebbe alcun contatore.
        </Alert>
      )}

      {result?.enforced && result.allowed && (
        <Alert variant="info" title="L'operazione passerebbe">
          Controvalore stimato: {result.estimatedAmount ?? 'non calcolabile'} {result.currency}.
        </Alert>
      )}

      {result?.enforced && !result.allowed && (
        <Alert
          variant="error"
          title="L'operazione verrebbe rifiutata"
          items={result.violations.map((violation) => violation.message)}
        />
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Pagina
// -----------------------------------------------------------------------------

export function TradingLimitsPage() {
  const [scope, setScope] = useState<ScopeFilter>('global');
  const [creating, setCreating] = useState(false);
  const portfolios = usePortfolios();
  const limits = useTradingLimits(scope);
  const updateSettings = useUpdateLimitSettings();

  const scopeOptions = useMemo(
    () => [
      { value: 'global', label: 'Globale (tutti i portafogli)' },
      { value: 'all', label: 'Tutti gli ambiti' },
      ...(portfolios.data?.items ?? []).map((portfolio) => ({ value: portfolio.id, label: portfolio.name })),
    ],
    [portfolios.data],
  );

  const targetPortfolioId = scope === 'global' || scope === 'all' ? null : scope;
  const settings = limits.data?.settings;

  return (
    <div className="trading-limits">
      <header className="page-header">
        <div>
          <h1>Limiti operativi</h1>
          <p className="page-header__subtitle">
            Il budget entro cui il trading può muoversi. Un'operazione concorre sempre sia al limite
            del suo portafoglio sia a quello globale: vince chi esaurisce per primo.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={scope === 'all'}>
          Nuovo limite
        </Button>
      </header>

      <Card>
        <div className="trading-limits__toolbar">
          <Select
            label="Ambito"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            options={scopeOptions}
            hint={scope === 'all' ? 'Vista di sola lettura: per creare un limite scegli un ambito preciso.' : undefined}
          />
          {settings && (
            <div className="trading-limits__setting">
              <Switch
                label="Applica i limiti anche agli ordini inviati a mano"
                checked={settings.enforceManualOrders}
                onChange={(checked) => updateSettings.mutate({ enforceManualOrders: checked })}
                disabled={updateSettings.isPending}
              />
              <p className="cell--minor">
                Gli ordini automatici sono soggetti ai limiti sempre, indipendentemente da questa
                impostazione. Giorno e mese si chiudono sul fuso {settings.timezone}.
              </p>
            </div>
          )}
        </div>
      </Card>

      {scope !== 'global' && scope !== 'all' && (limits.data?.globalCount ?? 0) > 0 && (
        <Alert variant="info" title="Esistono anche limiti globali">
          Un'operazione su questo portafoglio concorre anche ai {limits.data?.globalCount} limiti
          dell'ambito globale, che qui non sono elencati.
        </Alert>
      )}

      {limits.isLoading && <Spinner label="Caricamento dei limiti…" />}

      {limits.isError && (
        <Alert variant="error" title="Limiti non disponibili">
          {errorMessage(limits.error, 'Errore imprevisto')}
        </Alert>
      )}

      {limits.data && limits.data.items.length === 0 && (
        <EmptyState
          icon="🛑"
          title="Nessun limite configurato in questo ambito"
          description="Finché non ne configuri uno, nulla frena il numero o l'importo delle operazioni inviate al broker."
          action={
            scope === 'all' ? undefined : <Button onClick={() => setCreating(true)}>Crea il primo limite</Button>
          }
        />
      )}

      {limits.data && limits.data.items.length > 0 && (
        <Card flush>
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Limite</th>
                  <th className="col--secondary">Soglia</th>
                  <th>Consumo nel periodo</th>
                  <th className="col--tertiary">Finestra</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {limits.data.items.map((limit) => (
                  <LimitRow key={limit.id} limit={limit} />
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      )}

      <Simulator portfolioId={targetPortfolioId} />

      {creating && <NewLimitModal portfolioId={targetPortfolioId} onClose={() => setCreating(false)} />}
    </div>
  );
}
